import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import {
  CapabilityBadge,
  OziraProductToolShelf,
  OziraShellDrawer,
  type CapabilityState,
  type ToolShelfItem,
} from 'ozira-shell-kit';
import { ConversationSidebar } from '../components/conversation/ConversationSidebar';
import { IconSend } from '../components/conversation/icons';
import type { ProductId } from '../App';
import {
  applyCodePatch,
  cancelCodeExecution,
  cancelCodeRun,
  createCodeExecution,
  createCodeProject,
  getCodeCapabilities,
  getCodeDeployments,
  getCodeExecution,
  getCodeFile,
  getCodeProblems,
  getCodeTasks,
  getCodeTree,
  listCodeProjects,
  newCodeRunId,
  proposeCode,
  saveCodeFile,
  type CodeAgentResponse,
  type CodeCapabilities,
  type CodeDeploymentProvider,
  type CodePatchPreview,
  type CodeProblem,
  type CodeProject,
  type CodeTask,
  type CodeTree,
  type ExecutionRecord,
} from '../lib/codeApi';
import {
  approveForwardRun,
  cancelForwardRun,
  createForwardPlan,
  getForwardCapabilities,
  getForwardRun,
  listForwardPlans,
  listForwardRuns,
  rejectForwardRun,
  runForwardPlan,
  type ForwardCapabilities,
  type ForwardPlan,
  type ForwardRun,
  type ForwardTask,
  type ForwardTaskInput,
} from '../lib/forwardApi';
import { getIntelligenceSignals, getIntelligenceWhoami } from '../lib/intelligenceApi';

// Shared left rail for Code/Design/Forward/P -- the exact same sidebar Chat
// renders (username, credits, the Chat/Code/Design/Forward switcher), just
// with a product-specific "recent" slot instead of conversations/Projects.
// Wraps the existing Figma-approved workspace content rather than replacing
// it, so no approved layout is removed. Takes organizationTenantId as a
// plain prop (App.tsx already reads it off useSession() once at the top)
// rather than calling useSession() again here, matching how
// AssistantScreen already hands it to ConversationSidebar.
export function ProductSidebarShell({ productId, organizationTenantId, onNavigateProduct, onOpenSettings, recentSlot, children }: {
  productId: ProductId;
  organizationTenantId: string | null;
  onNavigateProduct: (productId: ProductId) => void;
  onOpenSettings?: () => void;
  recentSlot: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="ozira-cw-layout">
      <ConversationSidebar
        organizationTenantId={organizationTenantId}
        activeProductId={productId}
        onNavigateProduct={(id) => onNavigateProduct(id as ProductId)}
        onOpenSettings={onOpenSettings}
        recentSlot={recentSlot}
      />
      <div className="ozira-cw-main">{children}</div>
    </div>
  );
}

// A real recent-items list (name + last-updated only, never clickable unless
// the destination can actually be opened), or an honest empty state when a
// product has no backend for this yet -- never a fabricated control.
export function RecentFilesSection({ title, emptyMessage, items, onSelectItem }: {
  title: string;
  emptyMessage: string;
  items?: Array<{ id: string; label: string; meta?: string }>;
  onSelectItem?: (id: string) => void;
}) {
  return (
    <section className="ozira-cw-section ozira-cw-section--grow">
      <div className="ozira-cw-section-head">
        <h3>{title}</h3>
      </div>
      {!items || items.length === 0 ? (
        <p className="ozira-cw-empty-hint">{emptyMessage}</p>
      ) : (
        <ul className="ozira-cw-list">
          {items.map((item) => (
            <li key={item.id}>
              {onSelectItem ? (
                <button type="button" className="ozira-cw-row" onClick={() => onSelectItem(item.id)}>
                  <span className="ozira-cw-row-title">{item.label}</span>
                  {item.meta ? <span className="ozira-cw-row-meta">{item.meta}</span> : null}
                </button>
              ) : (
                <div className="ozira-cw-row">
                  <span className="ozira-cw-row-title">{item.label}</span>
                  {item.meta ? <span className="ozira-cw-row-meta">{item.meta}</span> : null}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const colors = {
  surface: 'var(--ozira-color-surface, #ffffff)',
  surfaceAlt: 'var(--ozira-color-cardAlt, #f2f2f5)',
  border: 'var(--ozira-color-border, #e0e3e8)',
  text: 'var(--ozira-color-text, #0e0f12)',
  muted: 'var(--ozira-color-muted, #6b707a)',
  code: 'var(--ozira-color-codeSurface, #14161a)',
  codeText: 'var(--ozira-color-codeText, #f7f8fa)',
  codeMuted: 'var(--ozira-color-codeMuted, #a8adb7)',
} as const;

const workspaceStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--ozira-space-md, 16px)',
  inlineSize: '100%',
  minInlineSize: 0,
};

const workspaceRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'stretch',
  gap: 'var(--ozira-space-md, 16px)',
  inlineSize: '100%',
  minInlineSize: 0,
};

const panelStyle: CSSProperties = {
  minInlineSize: 0,
  minBlockSize: '29.375rem',
  border: `1px solid ${colors.border}`,
  borderRadius: 'var(--ozira-radius-md, 12px)',
  background: colors.surface,
};

const mainPanelStyle: CSSProperties = {
  ...panelStyle,
  display: 'flex',
  flexDirection: 'column',
  padding: 'var(--ozira-space-md, 16px)',
};

const panelHeaderStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--ozira-space-sm, 8px)',
  marginBlockEnd: 'var(--ozira-space-md, 16px)',
};

const panelTitleStyle: CSSProperties = {
  margin: 0,
  color: colors.text,
  fontSize: 'var(--ozira-font-size-md, 14px)',
  fontWeight: 600,
};

const mutedTextStyle: CSSProperties = {
  margin: 0,
  color: colors.muted,
  fontSize: 'var(--ozira-font-size-sm, 12px)',
};

const calloutStyle: CSSProperties = {
  display: 'grid',
  gap: 'var(--ozira-space-sm, 8px)',
  marginBlockStart: 'var(--ozira-space-lg, 24px)',
  padding: 'var(--ozira-space-md, 16px)',
  border: `1px dashed ${colors.border}`,
  borderRadius: 'var(--ozira-radius-md, 12px)',
  background: colors.surfaceAlt,
};


function PanelList({ items, dark = false, label }: {
  items: readonly string[];
  dark?: boolean;
  label: string;
}) {
  return (
    <ul className={`ozira-panel-list${dark ? ' ozira-panel-list--dark' : ''}`} aria-label={label}>
      {items.map((item) => (
        <li key={item}>
          <button type="button" disabled>{item}</button>
        </li>
      ))}
    </ul>
  );
}

function TruthfulState({
  state,
  title,
  children,
  dark = false,
}: {
  state: CapabilityState;
  title: string;
  children: ReactNode;
  dark?: boolean;
}) {
  return (
    <div
      className="ozira-truthful-state"
      style={{
        ...calloutStyle,
        background: dark ? 'rgba(255, 255, 255, 0.045)' : calloutStyle.background,
        borderColor: dark ? 'rgba(255, 255, 255, 0.2)' : colors.border,
      }}
      role="status"
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--ozira-space-sm, 8px)' }}>
        <strong style={{ color: dark ? colors.codeText : colors.text, fontSize: 'var(--ozira-font-size-sm, 12px)' }}>
          {title}
        </strong>
        <CapabilityBadge state={state} />
      </div>
      <p style={{ ...mutedTextStyle, color: dark ? colors.codeMuted : colors.muted }}>{children}</p>
    </div>
  );
}

function DrawerPanel({
  title,
  side = 'start',
  state,
  emptyTitle,
  emptyDescription,
  style,
  figmaNode,
  items = [],
  mobileOpen = false,
  onMobileClose,
}: {
  title: string;
  side?: 'start' | 'end';
  state: CapabilityState;
  emptyTitle: string;
  emptyDescription: string;
  style: CSSProperties;
  figmaNode: string;
  items?: readonly string[];
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}) {
  return (
    <aside
      className={`ozira-product-secondary-pane${mobileOpen ? ' is-mobile-open' : ''}`}
      style={{ ...panelStyle, ...style }}
      data-figma-node={figmaNode}
      id={mobilePaneId(title)}
      role={mobileOpen ? 'dialog' : undefined}
      aria-modal={mobileOpen ? true : undefined}
      aria-label={mobileOpen ? title : undefined}
    >
      <button type="button" className="ozira-mobile-sheet-close" onClick={onMobileClose} aria-label={`Close ${title}`}>
        Close
      </button>
      <OziraShellDrawer title={title} side={side}>
        {items.length ? <PanelList items={items} label={`${title} navigation`} /> : null}
        <TruthfulState state={state} title={emptyTitle}>{emptyDescription}</TruthfulState>
      </OziraShellDrawer>
    </aside>
  );
}

const COMPACT_WORKSPACE_QUERY = '(max-width: 1199px)';
const FOCUSABLE_SELECTOR = 'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

function mobilePaneId(label: string): string {
  return `ozira-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-panel`;
}

export function useMobilePane() {
  const [pane, setPane] = useState<string | null>(null);
  const [compact, setCompact] = useState(() => (
    typeof window === 'undefined' || !window.matchMedia
      ? true
      : window.matchMedia(COMPACT_WORKSPACE_QUERY).matches
  ));
  const activePane = compact ? pane : null;

  const close = (restoreFocus: boolean) => {
    const closingPane = activePane;
    setPane(null);
    if (restoreFocus && closingPane && typeof window !== 'undefined') {
      window.setTimeout(() => {
        document.querySelector<HTMLButtonElement>(`[data-mobile-pane-trigger="${closingPane}"]`)?.focus();
      }, 0);
    }
  };
  const closePane = () => close(true);
  const closePaneWithoutRestore = () => close(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const media = window.matchMedia(COMPACT_WORKSPACE_QUERY);
    const onChange = (event: MediaQueryListEvent) => {
      const hadOpenPane = Boolean(document.querySelector('.ozira-product-secondary-pane.is-mobile-open, .ozira-cw-layout.is-mobile-sidebar-open'));
      setCompact(event.matches);
      if (!event.matches) {
        setPane(null);
        if (hadOpenPane) {
          window.setTimeout(() => {
            document.querySelector<HTMLElement>('.ozira-product-switcher__item.is-current')?.focus();
          }, 0);
        }
      }
    };
    setCompact(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!activePane) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTimer = window.setTimeout(() => {
      document.querySelector<HTMLElement>(
        '.ozira-product-secondary-pane.is-mobile-open .ozira-mobile-sheet-close, .ozira-cw-layout.is-mobile-sidebar-open .ozira-cw-search-bar__collapse',
      )?.focus();
    }, 0);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closePane();
      if (event.key !== 'Tab') return;
      const dialog = document.querySelector<HTMLElement>(
        '.ozira-product-secondary-pane.is-mobile-open, .ozira-cw-layout.is-mobile-sidebar-open .ozira-cw-sidebar',
      );
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = document.activeElement;
      if (event.shiftKey && (current === first || !dialog.contains(current))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (current === last || !dialog.contains(current))) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [activePane]);

  return { pane: activePane, openPane: setPane, closePane, closePaneWithoutRestore };
}

export function MobilePaneToolbar({ labels, activePane, onOpen }: {
  labels: readonly string[];
  activePane: string | null;
  onOpen: (label: string) => void;
}) {
  return (
    <nav
      className="ozira-mobile-pane-toolbar"
      aria-label="Workspace panels"
      inert={activePane ? '' : undefined}
      aria-hidden={activePane ? true : undefined}
    >
      {labels.map((label) => (
        <button
          key={label}
          type="button"
          data-mobile-pane-trigger={label}
          aria-controls={mobilePaneId(label)}
          aria-expanded={activePane === label}
          onClick={() => onOpen(label)}
        >
          {label}
        </button>
      ))}
    </nav>
  );
}

export function MobileSheetBackdrop({ open, onClose }: { open: boolean; onClose: () => void }) {
  return open ? (
    <button type="button" className="ozira-mobile-sheet-backdrop" aria-label="Close workspace panel" onClick={onClose} />
  ) : null;
}

type CodeChatEntry =
  | { id: string; role: 'user'; text: string }
  | { id: string; role: 'agent'; response: CodeAgentResponse }
  | { id: string; role: 'agent'; error: string };

const codeSmallButtonStyle: CSSProperties = {
  padding: '4px 10px',
  borderRadius: 'var(--ozira-radius-sm, 6px)',
  border: `1px solid ${colors.border}`,
  background: colors.surface,
  color: colors.text,
  fontSize: 'var(--ozira-font-size-sm, 12px)',
  cursor: 'pointer',
};

function unifiedDiffLines(diff: string): Array<{ type: 'add' | 'del' | 'ctx' | 'head'; text: string }> {
  return diff.split('\n').map((line) => {
    if (line.startsWith('+++') || line.startsWith('---')) return { type: 'head' as const, text: line };
    if (line.startsWith('+')) return { type: 'add' as const, text: line };
    if (line.startsWith('-')) return { type: 'del' as const, text: line };
    return { type: 'ctx' as const, text: line };
  });
}

// A code_execution or PATCH reply renders inline in the thread, the same
// way Chat renders any other structured reply -- Code has no separate
// "Editor + Diff" surface for step one; that's real functionality
// (open/edit any file, run tasks/terminal/deployments) intentionally held
// back for a later pass, not fabricated here.
function CodeReplyContent({
  entry, pendingPatch, onApplyPatch, onDiscardPatch, applyingPatch, applyError,
}: {
  entry: Extract<CodeChatEntry, { role: 'agent' }>;
  pendingPatch: { patch: string; preview: CodePatchPreview } | null;
  onApplyPatch: () => void;
  onDiscardPatch: () => void;
  applyingPatch: boolean;
  applyError: string;
}) {
  if ('error' in entry) return <p style={{ margin: 0 }}>{entry.error}</p>;
  const response = entry.response;
  if (response.type === 'MESSAGE') return <p style={{ margin: 0 }}>{response.message}</p>;
  if (response.type === 'ERROR') return <p style={{ margin: 0 }}>{response.message}</p>;
  if (response.type === 'PLAN') {
    return (
      <div>
        <p style={{ margin: '0 0 4px' }}><strong>Plan:</strong> {response.plan.appType || 'Untitled'}</p>
        {response.plan.pages.length > 0 ? <p style={{ margin: 0 }}>Pages: {response.plan.pages.join(', ')}</p> : null}
        {response.plan.notes ? <p style={{ margin: '4px 0 0' }}>{response.plan.notes}</p> : null}
      </div>
    );
  }
  if (response.type === 'PATCH') {
    // Only the most recent, still-pending patch shows live Apply/Discard --
    // an older turn whose patch was already applied or discarded just
    // states what it proposed, matching how Chat never re-offers a stale
    // action on an earlier message.
    const isCurrent = pendingPatch?.patch === response.patch;
    return (
      <div style={{ display: 'grid', gap: 8 }}>
        <p style={{ margin: 0 }}>Proposed changes to {response.preview.files.join(', ') || 'the project'}.</p>
        {isCurrent ? (
          <div style={{ display: 'grid', gap: 8 }}>
            {applyError ? <p style={{ margin: 0, color: '#b3261e' }}>{applyError}</p> : null}
            {response.preview.diff && response.preview.diff.length > 0 ? (
              <div style={{ overflow: 'auto', fontFamily: 'monospace', fontSize: 12, background: colors.code, color: colors.codeText, borderRadius: 'var(--ozira-radius-sm, 6px)', padding: 10 }}>
                {response.preview.diff.map((fileDiff, index) => (
                  <pre key={index} style={{ margin: index === 0 ? 0 : '10px 0 0', whiteSpace: 'pre-wrap' }}>
                    {unifiedDiffLines(fileDiff).map((line, lineIndex) => (
                      <div key={lineIndex} style={{ color: line.type === 'add' ? '#7ee787' : line.type === 'del' ? '#ff8a80' : line.type === 'head' ? colors.codeMuted : 'inherit' }}>
                        {line.text || ' '}
                      </div>
                    ))}
                  </pre>
                ))}
              </div>
            ) : null}
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={onDiscardPatch} disabled={applyingPatch} style={codeSmallButtonStyle}>Discard</button>
              <button type="button" onClick={onApplyPatch} disabled={applyingPatch} style={{ ...codeSmallButtonStyle, background: 'var(--ozira-color-primary, #ce1c40)', color: '#fff', borderColor: 'transparent' }}>
                {applyingPatch ? 'Applying…' : 'Apply'}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );
  }
  // EXECUTION
  const execution = response.execution;
  return (
    <div style={{ fontFamily: 'monospace', fontSize: 12 }}>
      <p style={{ margin: '0 0 4px' }}>$ {execution.command || '(command)'} — {execution.status}</p>
      {execution.stdout ? <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{execution.stdout}</pre> : null}
      {execution.stderr ? <pre style={{ margin: 0, whiteSpace: 'pre-wrap', color: '#ff8a80' }}>{execution.stderr}</pre> : null}
    </div>
  );
}

// Step two: real file browsing/editing (GET/POST /api/code/file) and real
// execution/task-running (GET /api/code/tasks|problems|deployments,
// POST /api/executions), layered on the chat-first design as overlays
// instead of the old always-visible three-column layout.
function CodeFileBrowserModal({ tree, onSelectFile, onClose }: {
  tree: CodeTree | null;
  onSelectFile: (path: string) => void;
  onClose: () => void;
}) {
  return (
    <div className="ozira-cw-modal-backdrop" role="presentation" onClick={onClose}>
      <div className="ozira-cw-panel" role="dialog" aria-modal="true" aria-label="Files" onClick={(event) => event.stopPropagation()}>
        <div className="ozira-cw-panel-head">
          <h3>Files</h3>
        </div>
        {!tree || tree.files.length === 0 ? (
          <p className="ozira-cw-empty-hint">No files yet.</p>
        ) : (
          <ul className="ozira-cw-tool-list" aria-label="Files">
            {tree.files.map((file) => (
              <li key={file.path}>
                <button type="button" className="ozira-cw-tool-row" onClick={() => onSelectFile(file.path)}>
                  {file.path}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function CodeFileEditorOverlay({ projectId, path, onClose, onSaved }: {
  projectId: string;
  path: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [content, setContent] = useState('');
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    getCodeFile(projectId, path).then((file) => {
      if (cancelled) return;
      setContent(file.content);
      setDraft(file.content);
    }).catch((err) => { if (!cancelled) setError(err?.message || 'Could not open file.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, path]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const dirty = draft !== content;

  const handleSave = useCallback(() => {
    if (!dirty || saving) return;
    setSaving(true);
    setError('');
    saveCodeFile(projectId, path, draft).then(() => {
      setContent(draft);
      onSaved();
    }).catch((err) => setError(err?.message || 'Could not save file.'))
      .finally(() => setSaving(false));
  }, [dirty, saving, projectId, path, draft, onSaved]);

  return (
    <div className="ozira-cw-code-editor-backdrop" role="presentation" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="ozira-cw-code-editor" role="dialog" aria-modal="true" aria-label={path}>
        <div className="ozira-cw-code-editor__top">
          <button type="button" className="ozira-cw-code-editor__close" aria-label="Close" onClick={onClose}>×</button>
          <div className="ozira-cw-code-editor__title">{path}</div>
          <button type="button" className="ozira-cw-code-editor__save" onClick={handleSave} disabled={!dirty || saving}>
            {saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}
          </button>
        </div>
        <div className="ozira-cw-code-editor__body">
          {error ? <p className="ozira-cw-code-editor__error" role="alert">{error}</p> : null}
          {loading ? (
            <p style={{ color: '#fff' }}>Loading…</p>
          ) : (
            <textarea
              className="ozira-cw-code-editor__textarea"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              spellCheck={false}
            />
          )}
        </div>
      </div>
    </div>
  );
}

const EXECUTION_TERMINAL_STATUSES = ['succeeded', 'failed', 'cancelled', 'denied', 'timed_out'];

function ExecutionOutput({ execution, onCancel }: { execution: ExecutionRecord; onCancel: () => void }) {
  const terminal = EXECUTION_TERMINAL_STATUSES.includes(String(execution.status).toLowerCase());
  return (
    <div style={{ fontFamily: 'monospace', fontSize: 12, display: 'grid', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <p style={{ margin: 0 }}>$ {execution.command || '(command)'} — {execution.status}</p>
        {!terminal ? <button type="button" onClick={onCancel} style={codeSmallButtonStyle}>Cancel</button> : null}
      </div>
      {execution.stdout ? <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{execution.stdout}</pre> : null}
      {execution.stderr ? <pre style={{ margin: 0, whiteSpace: 'pre-wrap', color: '#ff8a80' }}>{execution.stderr}</pre> : null}
    </div>
  );
}

// Real Terminal/Problems/Tests/Deployments, backed by GET /api/code/tasks,
// /problems, /deployments and POST /api/executions -- capabilities().reason
// is shown verbatim rather than a canned "Execution unavailable" string, so
// this is honest about the runner's actual state in this deployment. One
// overlay with an internal tab switcher, matching how these four views were
// always a single panel, not four separate entry points.
function CodeExecutionOverlay({ projectId, capabilities, onClose }: {
  projectId: string;
  capabilities: CodeCapabilities | null;
  onClose: () => void;
}) {
  const [activeView, setActiveView] = useState<'tasks' | 'problems' | 'deployments' | 'terminal'>('tasks');
  const [tasks, setTasks] = useState<CodeTask[]>([]);
  const [problems, setProblems] = useState<{ problems: CodeProblem[]; counts: { error: number; warning: number; info: number } } | null>(null);
  const [deployments, setDeployments] = useState<{ providers: CodeDeploymentProvider[]; history: unknown[] } | null>(null);
  const [runningTaskId, setRunningTaskId] = useState<string | null>(null);
  const [terminalCommand, setTerminalCommand] = useState('');
  const [activeExecution, setActiveExecution] = useState<ExecutionRecord | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    getCodeTasks(projectId).then((result) => setTasks(result.tasks)).catch(() => setTasks([]));
    getCodeProblems(projectId).then(setProblems).catch(() => setProblems(null));
    getCodeDeployments(projectId).then(setDeployments).catch(() => setDeployments(null));
  }, [projectId]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const pollExecution = useCallback((executionId: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      getCodeExecution(executionId).then(({ execution }) => {
        setActiveExecution(execution);
        if (EXECUTION_TERMINAL_STATUSES.includes(String(execution.status).toLowerCase())) {
          if (pollRef.current) clearInterval(pollRef.current);
          setRunningTaskId(null);
        }
      }).catch(() => {
        if (pollRef.current) clearInterval(pollRef.current);
        setRunningTaskId(null);
      });
    }, 1500);
  }, []);

  const handleRunTask = useCallback((task: CodeTask) => {
    setRunningTaskId(task.id);
    createCodeExecution({ projectId, kind: task.kind, taskId: task.id, reason: `Run ${task.label}` }).then(({ execution }) => {
      setActiveExecution(execution);
      pollExecution(execution.id);
    }).catch(() => setRunningTaskId(null));
  }, [projectId, pollExecution]);

  const handleRunTerminal = useCallback(() => {
    if (!terminalCommand.trim()) return;
    const [executable, ...args] = terminalCommand.trim().split(/\s+/);
    setRunningTaskId('terminal');
    createCodeExecution({ projectId, kind: 'terminal', executable, args, reason: 'Terminal command' }).then(({ execution }) => {
      setActiveExecution(execution);
      pollExecution(execution.id);
    }).catch(() => setRunningTaskId(null));
  }, [projectId, terminalCommand, pollExecution]);

  const handleCancel = useCallback(() => {
    if (!activeExecution) return;
    cancelCodeExecution(activeExecution.id).catch(() => {});
  }, [activeExecution]);

  const runnerAvailable = Boolean(capabilities?.execution.runnerAvailable);
  const tools: ToolShelfItem[] = [
    { id: 'terminal', label: 'Terminal', state: runnerAvailable ? 'LIVE' : 'BLOCKED', note: runnerAvailable ? undefined : capabilities?.execution.reason || 'Execution unavailable' },
    { id: 'tasks', label: 'Tests', state: runnerAvailable ? 'LIVE' : 'BLOCKED', note: runnerAvailable ? undefined : capabilities?.execution.reason || 'Execution unavailable' },
    { id: 'problems', label: 'Problems', state: 'LIVE' },
    { id: 'deployments', label: 'Deployments', state: 'LIVE' },
  ];

  return (
    <div className="ozira-cw-code-editor-backdrop" role="presentation" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="ozira-cw-code-editor" role="dialog" aria-modal="true" aria-label="Execution">
        <div className="ozira-cw-code-editor__top">
          <button type="button" className="ozira-cw-code-editor__close" aria-label="Close" onClick={onClose}>×</button>
          <div className="ozira-cw-code-editor__title">Execution</div>
        </div>
        <div className="ozira-cw-code-editor__body ozira-cw-code-editor__body--panel">
          <OziraProductToolShelf
            tools={tools}
            onSelect={(id) => setActiveView(id as 'tasks' | 'problems' | 'deployments' | 'terminal')}
          />
          {activeView === 'terminal' ? (
            runnerAvailable ? (
              <div style={{ display: 'grid', gap: 6, marginBlockStart: 8 }}>
                <form onSubmit={(event) => { event.preventDefault(); handleRunTerminal(); }} style={{ display: 'flex', gap: 6 }}>
                  <input
                    type="text"
                    value={terminalCommand}
                    onChange={(event) => setTerminalCommand(event.target.value)}
                    placeholder="npm test"
                    style={{ flex: '1 1 auto', padding: '6px 8px', borderRadius: 'var(--ozira-radius-sm, 6px)', border: '1px solid rgba(255,255,255,0.16)', fontFamily: 'monospace', background: 'rgba(255,255,255,0.05)', color: '#fff' }}
                  />
                  <button type="submit" disabled={!terminalCommand.trim() || runningTaskId === 'terminal'} style={codeSmallButtonStyle}>
                    {runningTaskId === 'terminal' ? 'Running…' : 'Run'}
                  </button>
                </form>
                {activeExecution ? <ExecutionOutput execution={activeExecution} onCancel={handleCancel} /> : null}
              </div>
            ) : (
              <p style={{ color: '#fff' }}>{capabilities?.execution.reason || 'Execution unavailable'}</p>
            )
          ) : activeView === 'tasks' ? (
            runnerAvailable ? (
              <div style={{ display: 'grid', gap: 8, marginBlockStart: 8 }}>
                {tasks.length === 0 ? (
                  <p style={{ color: '#fff' }}>No project scripts detected (test/lint/typecheck/build/format in package.json).</p>
                ) : (
                  <ul className="ozira-panel-list" aria-label="Tasks">
                    {tasks.map((task) => (
                      <li key={task.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <span>{task.label}{task.blockedReason ? ` — ${task.blockedReason}` : ''}</span>
                        <button type="button" disabled={!task.enabled || runningTaskId === task.id} onClick={() => handleRunTask(task)} style={codeSmallButtonStyle}>
                          {runningTaskId === task.id ? 'Running…' : 'Run'}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {activeExecution ? <ExecutionOutput execution={activeExecution} onCancel={handleCancel} /> : null}
              </div>
            ) : (
              <p style={{ color: '#fff' }}>{capabilities?.execution.reason || 'Execution unavailable'}</p>
            )
          ) : activeView === 'problems' ? (
            !problems || problems.problems.length === 0 ? (
              <p style={{ color: '#fff' }}>No problems recorded for this project.</p>
            ) : (
              <ul className="ozira-panel-list" aria-label="Problems">
                {problems.problems.map((problem) => (
                  <li key={problem.id}>{problem.severity}: {problem.message}{problem.file ? ` (${problem.file}${problem.line ? `:${problem.line}` : ''})` : ''}</li>
                ))}
              </ul>
            )
          ) : !deployments || deployments.providers.every((provider) => !provider.configured) ? (
            <p style={{ color: '#fff' }}>No deployment provider is configured for this environment.</p>
          ) : (
            <ul className="ozira-panel-list" aria-label="Deployment providers">
              {deployments.providers.filter((provider) => provider.configured).map((provider) => (
                <li key={provider.id}>{provider.label}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export function CodeWorkspace({ onNavigateProduct, onOpenSettings, organizationTenantId = null }: { onNavigateProduct: (productId: ProductId) => void; onOpenSettings?: () => void; organizationTenantId?: string | null }) {
  const [projects, setProjects] = useState<CodeProject[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const [showNewProjectForm, setShowNewProjectForm] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectTemplate, setNewProjectTemplate] = useState<'empty' | 'web' | 'node'>('empty');
  const [creatingProject, setCreatingProject] = useState(false);
  const [createProjectError, setCreateProjectError] = useState('');

  const [tree, setTree] = useState<CodeTree | null>(null);

  const [chat, setChat] = useState<CodeChatEntry[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [sending, setSending] = useState(false);
  const runIdRef = useRef<string | null>(null);
  const [pendingPatch, setPendingPatch] = useState<{ patch: string; preview: CodePatchPreview } | null>(null);
  const [applyingPatch, setApplyingPatch] = useState(false);
  const [applyError, setApplyError] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  const [fileBrowserOpen, setFileBrowserOpen] = useState(false);
  const [editingFilePath, setEditingFilePath] = useState<string | null>(null);
  const [executionOverlayOpen, setExecutionOverlayOpen] = useState(false);
  const [capabilities, setCapabilities] = useState<CodeCapabilities | null>(null);
  const [capabilitiesLoaded, setCapabilitiesLoaded] = useState(false);

  const selectedProject = projects.find((project) => project.id === selectedProjectId) || null;

  useEffect(() => {
    listCodeProjects().then((list) => {
      setProjects(list);
      if (list.length > 0) setSelectedProjectId((current) => current || list[0].id);
      else setShowNewProjectForm(true);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedProjectId) { setTree(null); return; }
    getCodeTree(selectedProjectId).then(setTree).catch(() => setTree(null));
  }, [selectedProjectId]);

  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [chat.length]);

  useEffect(() => {
    if (executionOverlayOpen && !capabilitiesLoaded) {
      setCapabilitiesLoaded(true);
      getCodeCapabilities().then(setCapabilities).catch(() => {});
    }
  }, [executionOverlayOpen, capabilitiesLoaded]);

  const handleCreateProject = useCallback((name: string, template: 'empty' | 'web' | 'node') => {
    setCreatingProject(true);
    setCreateProjectError('');
    createCodeProject({ name, template }).then((project) => {
      setProjects((prev) => [project, ...prev]);
      setSelectedProjectId(project.id);
      setShowNewProjectForm(false);
    }).catch((error) => {
      setCreateProjectError(error?.message || 'Could not create project.');
    }).finally(() => setCreatingProject(false));
  }, []);

  const handleSend = useCallback(() => {
    if (!selectedProjectId || !chatInput.trim() || sending) return;
    const prompt = chatInput.trim();
    const runId = newCodeRunId();
    runIdRef.current = runId;
    setChat((prev) => [...prev, { id: runId + '_u', role: 'user', text: prompt }]);
    setChatInput('');
    setSending(true);
    proposeCode({ project: selectedProjectId, prompt, runId }).then((response) => {
      setChat((prev) => [...prev, { id: runId + '_a', role: 'agent', response }]);
      if (response.type === 'PATCH') setPendingPatch({ patch: response.patch, preview: response.preview });
    }).catch((error) => {
      setChat((prev) => [...prev, { id: runId + '_e', role: 'agent', error: error?.message || 'The agent could not complete the request.' }]);
    }).finally(() => {
      setSending(false);
      runIdRef.current = null;
    });
  }, [selectedProjectId, chatInput, sending]);

  const handleCancelChat = useCallback(() => {
    if (runIdRef.current) cancelCodeRun(runIdRef.current).catch(() => {});
  }, []);

  const handleApplyPatch = useCallback(() => {
    if (!selectedProjectId || !pendingPatch) return;
    setApplyingPatch(true);
    setApplyError('');
    applyCodePatch(selectedProjectId, pendingPatch.patch).then((result) => {
      if (!result.ok) { setApplyError(result.result.message || 'The patch could not be applied.'); return; }
      setPendingPatch(null);
      getCodeTree(selectedProjectId).then(setTree).catch(() => {});
    }).catch((error) => setApplyError(error?.message || 'The patch could not be applied.'))
      .finally(() => setApplyingPatch(false));
  }, [selectedProjectId, pendingPatch]);

  const handleDiscardPatch = useCallback(() => setPendingPatch(null), []);

  const handleFileSaved = useCallback(() => {
    if (!selectedProjectId) return;
    getCodeTree(selectedProjectId).then(setTree).catch(() => {});
  }, [selectedProjectId]);

  return (
    <>
    <ProductSidebarShell
      productId="code"
      organizationTenantId={organizationTenantId}
      onNavigateProduct={onNavigateProduct}
      onOpenSettings={onOpenSettings}
      recentSlot={(
        <RecentFilesSection
          title="Recent files"
          emptyMessage="No files yet."
          items={tree?.files.slice(0, 8).map((file) => ({ id: file.path, label: file.path })) || []}
          onSelectItem={selectedProjectId ? (id) => setEditingFilePath(id) : undefined}
        />
      )}
    >
      <section aria-label="OZIRA Code repository workspace" data-figma-node="66:184" style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
        <header className="ozira-cw-header">
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <h2 className="ozira-cw-header-title">OZIRA Code</h2>
            <p style={{ margin: 0, fontSize: 'var(--ozira-font-size-xs, 11px)', color: 'var(--ozira-color-muted)' }}>
              {selectedProject ? selectedProject.name : 'No project selected'}
            </p>
          </div>
          <div className="ozira-cw-header-spacer" />
          <div className="ozira-cw-header-menu">
            <button
              type="button"
              className="ozira-cw-header-btn"
              aria-label="Files"
              title="Files"
              disabled={!selectedProjectId}
              onClick={() => setFileBrowserOpen(true)}
            >
              Files
            </button>
            <button
              type="button"
              className="ozira-cw-header-btn"
              aria-label="Execution"
              title="Execution"
              disabled={!selectedProjectId}
              onClick={() => setExecutionOverlayOpen(true)}
            >
              Execution
            </button>
            <button
              type="button"
              className="ozira-cw-header-btn"
              aria-label="Project options"
              title="Project options"
              aria-expanded={projectMenuOpen}
              onClick={() => setProjectMenuOpen((open) => !open)}
            >
              ···
            </button>
            {projectMenuOpen ? (
              <ul className="ozira-cw-menu" role="menu">
                {projects.map((project) => (
                  <li role="none" key={project.id}>
                    <button
                      type="button"
                      role="menuitem"
                      aria-current={project.id === selectedProjectId ? 'true' : undefined}
                      onClick={() => { setSelectedProjectId(project.id); setProjectMenuOpen(false); }}
                    >
                      {project.name}
                    </button>
                  </li>
                ))}
                <li role="none">
                  <button type="button" role="menuitem" onClick={() => { setProjectMenuOpen(false); setShowNewProjectForm(true); }}>
                    New project
                  </button>
                </li>
              </ul>
            ) : null}
          </div>
        </header>

        {showNewProjectForm ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (!newProjectName.trim() || creatingProject) return;
              handleCreateProject(newProjectName.trim(), newProjectTemplate);
              setNewProjectName('');
            }}
            style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid var(--ozira-color-border)' }}
          >
            <input
              type="text"
              value={newProjectName}
              onChange={(event) => setNewProjectName(event.target.value)}
              placeholder="New project name"
              autoFocus
              style={{ flex: '1 1 12rem', font: 'inherit', padding: '6px 10px', borderRadius: 'var(--ozira-radius-sm, 6px)', border: '1px solid var(--ozira-color-border)' }}
            />
            <select
              value={newProjectTemplate}
              onChange={(event) => setNewProjectTemplate(event.target.value as 'empty' | 'web' | 'node')}
              style={{ font: 'inherit', padding: '6px 8px', borderRadius: 'var(--ozira-radius-sm, 6px)', border: '1px solid var(--ozira-color-border)' }}
            >
              <option value="empty">Empty</option>
              <option value="web">Web (HTML/CSS/JS)</option>
              <option value="node">Node</option>
            </select>
            <button type="submit" disabled={!newProjectName.trim() || creatingProject} style={codeSmallButtonStyle}>
              {creatingProject ? 'Creating…' : 'New project'}
            </button>
            {projects.length > 0 ? (
              <button type="button" onClick={() => setShowNewProjectForm(false)} style={codeSmallButtonStyle}>Cancel</button>
            ) : null}
          </form>
        ) : null}
        {createProjectError ? <p role="alert" style={{ margin: '8px 16px 0', color: '#b3261e', fontSize: 'var(--ozira-font-size-xs, 11px)' }}>{createProjectError}</p> : null}

        <div className="ozira-cw-messages" role="log" aria-label="OZIRA Code" ref={listRef}>
          {!selectedProjectId ? (
            <p className="ozira-cw-empty-hint">Create a project to begin.</p>
          ) : chat.length === 0 ? (
            <p className="ozira-cw-empty-hint">Ask OZIRA Code to build or change something in this project.</p>
          ) : (
            chat.map((entry) => (
              entry.role === 'user' ? (
                <div className="ozira-cw-message-row ozira-cw-message-row--user" key={entry.id}>
                  <div className="ozira-cw-bubble ozira-cw-bubble--user">{entry.text}</div>
                </div>
              ) : (
                <div className="ozira-cw-message-row" key={entry.id}>
                  <div className="ozira-cw-assistant-block">
                    <div className="ozira-cw-bubble ozira-cw-bubble--assistant">
                      <CodeReplyContent
                        entry={entry}
                        pendingPatch={pendingPatch}
                        onApplyPatch={handleApplyPatch}
                        onDiscardPatch={handleDiscardPatch}
                        applyingPatch={applyingPatch}
                        applyError={applyError}
                      />
                    </div>
                  </div>
                </div>
              )
            ))
          )}
          {sending ? <p className="ozira-cw-empty-hint">Working…</p> : null}
        </div>

        <div className="ozira-cw-composer-wrap">
          <p className="ozira-cw-disclaimer">OZIRA can make mistakes. Check important info.</p>
          <form
            className="ozira-cw-composer"
            onSubmit={(event) => { event.preventDefault(); if (!sending) handleSend(); }}
          >
            <textarea
              className="ozira-cw-composer-input"
              rows={1}
              value={chatInput}
              onChange={(event) => setChatInput(event.target.value)}
              placeholder={selectedProjectId ? 'Ask OZIRA Code…' : 'Create a project first'}
              aria-label={selectedProjectId ? 'Ask OZIRA Code…' : 'Create a project first'}
              disabled={!selectedProjectId || sending}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  if (!sending) handleSend();
                }
              }}
            />
            {sending ? (
              <button type="button" className="ozira-cw-send" aria-label="Cancel" title="Cancel" onClick={handleCancelChat}>
                <span aria-hidden="true">■</span>
              </button>
            ) : (
              <button type="submit" className="ozira-cw-send" aria-label="Send" title="Send" disabled={!selectedProjectId || !chatInput.trim()}>
                <IconSend stroke="white" />
              </button>
            )}
          </form>
        </div>
      </section>
    </ProductSidebarShell>
    {fileBrowserOpen ? (
      <CodeFileBrowserModal
        tree={tree}
        onSelectFile={(path) => { setFileBrowserOpen(false); setEditingFilePath(path); }}
        onClose={() => setFileBrowserOpen(false)}
      />
    ) : null}
    {editingFilePath && selectedProjectId ? (
      <CodeFileEditorOverlay
        projectId={selectedProjectId}
        path={editingFilePath}
        onClose={() => setEditingFilePath(null)}
        onSaved={handleFileSaved}
      />
    ) : null}
    {executionOverlayOpen && selectedProjectId ? (
      <CodeExecutionOverlay
        projectId={selectedProjectId}
        capabilities={capabilities}
        onClose={() => setExecutionOverlayOpen(false)}
      />
    ) : null}
    </>
  );
}

// The bridge's own error codes (lib/intelligencebridge.js) map to a capability
// state: DESIGN_SLOT for "not turned on in this environment" (the operator
// hasn't configured it), DEGRADED for "configured but the remote service
// itself is failing right now".
function intelligenceBridgeCapabilityState(code: string): CapabilityState {
  if (code === 'BRIDGE_DISABLED' || code === 'BRIDGE_MISCONFIGURED') return 'DESIGN_SLOT';
  return 'DEGRADED';
}

function firstStringField(obj: Record<string, unknown>, keys: readonly string[]): string | undefined {
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return undefined;
}

// The remote shared-intelligence MCP tool's response shape isn't defined
// anywhere in this codebase (it's owned by a separate Databricks-hosted
// service) -- so this reads whatever real array and fields the payload
// actually contains, with plain fallbacks, rather than assuming a schema.
function extractSignalItems(payload: Record<string, unknown> | null): unknown[] | null {
  if (!payload) return null;
  for (const key of ['signals', 'items', 'results', 'data'] as const) {
    const value = payload[key];
    if (Array.isArray(value)) return value;
  }
  return null;
}

function describeSignalItem(item: unknown, index: number): { key: string; label: string; meta?: string } {
  if (item && typeof item === 'object' && !Array.isArray(item)) {
    const obj = item as Record<string, unknown>;
    const label = firstStringField(obj, ['title', 'name', 'summary', 'label']) || `Signal ${index + 1}`;
    const meta = firstStringField(obj, ['source', 'type', 'category', 'updatedAt', 'timestamp']);
    const key = firstStringField(obj, ['id']) || String(index);
    return { key, label, meta };
  }
  return { key: String(index), label: String(item) };
}

export function PWorkspace({ onNavigateProduct, onOpenSettings, organizationTenantId = null }: { onNavigateProduct: (productId: ProductId) => void; onOpenSettings?: () => void; organizationTenantId?: string | null }) {
  const mobile = useMobilePane();
  const [bridge, setBridge] = useState<
    | { status: 'loading' }
    | { status: 'unavailable'; code: string; error: string }
    | { status: 'live' }
  >({ status: 'loading' });
  const [signals, setSignals] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const whoami = await getIntelligenceWhoami();
      if (cancelled) return;
      if (!whoami.ok) {
        setBridge({ status: 'unavailable', code: whoami.code, error: whoami.error });
        return;
      }
      setBridge({ status: 'live' });
      const result = await getIntelligenceSignals();
      if (cancelled) return;
      if (result.ok) setSignals(result.data);
    })();
    return () => { cancelled = true; };
  }, []);

  const signalItems = bridge.status === 'live' ? extractSignalItems(signals) : null;

  return (
    <ProductSidebarShell
      productId="p"
      organizationTenantId={organizationTenantId}
      onNavigateProduct={onNavigateProduct}
      onOpenSettings={onOpenSettings}
      recentSlot={<RecentFilesSection title="Recent" emptyMessage="No sources connected yet." />}
    >
    <section className="ozira-product-workspace-layout" style={workspaceStyle} aria-label="OZIRA P intelligence workspace" data-figma-node="66:210">
      <MobilePaneToolbar labels={['Sources', 'Provenance']} activePane={mobile.pane} onOpen={mobile.openPane} />
      <MobileSheetBackdrop open={Boolean(mobile.pane)} onClose={mobile.closePane} />
      <div className="ozira-product-workspace-row ozira-p-workspace__row" style={workspaceRowStyle}>
        <DrawerPanel
          title="Sources"
          state="DESIGN_SLOT"
          emptyTitle="No sources connected"
          emptyDescription="Add a source to begin an evidence-backed investigation."
          figmaNode="66:288"
          items={['Feeds', 'Documents', 'Datasets', 'Signals', 'Saved investigations']}
          style={{ flex: '0.8 1 15rem', minBlockSize: '36.875rem' }}
          mobileOpen={mobile.pane === 'Sources'}
          onMobileClose={mobile.closePane}
        />

        <section
          style={{
            ...mainPanelStyle,
            flex: '2.75 1 36rem',
            minBlockSize: '36.875rem',
            background: colors.surfaceAlt,
          }}
          aria-label="Signals and analysis"
          data-figma-node="66:291"
          className="ozira-product-main-panel"
          inert={mobile.pane ? '' : undefined}
          aria-hidden={mobile.pane ? true : undefined}
        >
          <div style={panelHeaderStyle}>
            <div>
              <h2 style={panelTitleStyle}>Signals + Analysis</h2>
            </div>
            {bridge.status === 'loading' ? (
              <CapabilityBadge state="DESIGN_SLOT" note="Checking the shared intelligence bridge…" />
            ) : bridge.status === 'unavailable' ? (
              <CapabilityBadge state={intelligenceBridgeCapabilityState(bridge.code)} note={bridge.error} />
            ) : (
              <CapabilityBadge state="LIVE" note="Connected to the shared intelligence service." />
            )}
          </div>
          <PanelList
            label="Analysis views"
            items={['Evidence graph', 'Signal stream', 'Comparisons', 'Synthesis', 'Reports']}
          />
          {bridge.status === 'loading' ? (
            <TruthfulState state="DESIGN_SLOT" title="Checking shared intelligence">
              Confirming whether the shared intelligence bridge is reachable for your organization.
            </TruthfulState>
          ) : bridge.status === 'unavailable' ? (
            <TruthfulState state={intelligenceBridgeCapabilityState(bridge.code)} title="Shared intelligence unavailable">
              {bridge.error}
            </TruthfulState>
          ) : signalItems && signalItems.length > 0 ? (
            <ul style={{ display: 'grid', gap: 'var(--ozira-space-xs, 4px)', margin: 0, padding: 0, listStyle: 'none' }} aria-label="Signals">
              {signalItems.map((item, index) => {
                const described = describeSignalItem(item, index);
                return (
                  <li
                    key={described.key}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 'var(--ozira-space-sm, 8px)',
                      padding: 'var(--ozira-space-xs, 4px) 0',
                      borderBlockEnd: `1px solid ${colors.border}`,
                    }}
                  >
                    <span style={{ color: colors.text, fontSize: 'var(--ozira-font-size-sm, 12px)' }}>{described.label}</span>
                    {described.meta ? <span style={mutedTextStyle}>{described.meta}</span> : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <TruthfulState state="LIVE" title="No signals yet">
              The shared intelligence service returned no signals for your organization.
            </TruthfulState>
          )}
        </section>

        <DrawerPanel
          title="Provenance"
          side="end"
          state="BLOCKED"
          emptyTitle="No evidence selected"
          emptyDescription="Select evidence to view its source and history."
          figmaNode="66:296"
          items={['Source', 'Retrieved information', 'Inference', 'Verified fact', 'Memory relationship']}
          style={{ flex: '1.1 1 18rem', minBlockSize: '36.875rem' }}
          mobileOpen={mobile.pane === 'Provenance'}
          onMobileClose={mobile.closePane}
        />
      </div>
    </section>
    </ProductSidebarShell>
  );
}

const FORWARD_EXECUTION_KINDS = ['test', 'build', 'lint', 'typecheck', 'format', 'deploy'] as const;

function forwardTaskSummary(task: ForwardTask, codeProjects: CodeProject[], projectTasks: Record<string, CodeTask[]>): string {
  if (task.kind === 'code_execution') {
    const project = codeProjects.find((p) => p.id === task.projectId);
    const projectLabel = project ? project.name : 'a Code project';
    if (task.executionKind === 'deploy') return `Deploy ${projectLabel} via ${task.deploymentProvider || '…'}`;
    const registered = task.projectId ? projectTasks[task.projectId] : undefined;
    const found = registered?.find((t) => t.id === task.taskId);
    return `Run ${found ? found.label : (task.executionKind || '…')} on ${projectLabel}`;
  }
  if (task.kind === 'notify_email') return `Email ${task.to || '…'}`;
  if (task.kind === 'notify_telegram') return 'Notify via Telegram';
  return task.label || 'Manual approval';
}

function ForwardTaskBuilderRow({ task, index, onChange, onRemove, codeProjects, projectTasks, projectProviders, onProjectPicked }: {
  task: ForwardTaskInput & { key: string };
  index: number;
  onChange: (key: string, patch: Partial<ForwardTaskInput>) => void;
  onRemove: (key: string) => void;
  codeProjects: CodeProject[];
  projectTasks: Record<string, CodeTask[]>;
  projectProviders: Record<string, CodeDeploymentProvider[]>;
  onProjectPicked: (projectId: string) => void;
}) {
  const rowStyle: CSSProperties = { display: 'grid', gap: 'var(--ozira-space-xs, 4px)', padding: 'var(--ozira-space-sm, 8px)', border: `1px solid ${colors.border}`, borderRadius: 'var(--ozira-radius-sm, 8px)' };
  const inputStyle: CSSProperties = { font: 'inherit', padding: '6px 8px', border: `1px solid ${colors.border}`, borderRadius: 6 };
  return (
    <li style={rowStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--ozira-space-sm, 8px)' }}>
        <strong style={{ fontSize: 'var(--ozira-font-size-sm, 12px)' }}>{index + 1}. {task.kind === 'code_execution' ? 'Code execution' : task.kind === 'notify_email' ? 'Email' : task.kind === 'notify_telegram' ? 'Telegram' : 'Manual approval'}</strong>
        <button type="button" style={codeSmallButtonStyle} onClick={() => onRemove(task.key)}>Remove</button>
      </div>
      <select aria-label={`Task ${index + 1} kind`} value={task.kind} style={inputStyle} onChange={(e) => onChange(task.key, { kind: e.target.value as ForwardTaskInput['kind'] })}>
        <option value="code_execution">Code execution</option>
        <option value="notify_email">Email notification</option>
        <option value="notify_telegram">Telegram notification</option>
        <option value="approval">Manual approval</option>
      </select>
      {task.kind === 'code_execution' && (
        <>
          <select aria-label={`Task ${index + 1} project`} value={task.projectId || ''} style={inputStyle} onChange={(e) => { onChange(task.key, { projectId: e.target.value, taskId: undefined, deploymentProvider: undefined }); if (e.target.value) onProjectPicked(e.target.value); }}>
            <option value="">Choose a Code project…</option>
            {codeProjects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select aria-label={`Task ${index + 1} execution kind`} value={task.executionKind || ''} style={inputStyle} onChange={(e) => onChange(task.key, { executionKind: e.target.value as ForwardTaskInput['executionKind'], taskId: undefined, deploymentProvider: undefined })}>
            <option value="">Choose what to run…</option>
            {FORWARD_EXECUTION_KINDS.map((kind) => <option key={kind} value={kind}>{kind}</option>)}
          </select>
          {task.executionKind && task.executionKind !== 'deploy' && task.projectId && (
            <select aria-label={`Task ${index + 1} registered task`} value={task.taskId || ''} style={inputStyle} onChange={(e) => onChange(task.key, { taskId: e.target.value })}>
              <option value="">Choose a registered task…</option>
              {(projectTasks[task.projectId] || []).filter((t) => t.kind === task.executionKind).map((t) => <option key={t.id} value={t.id} disabled={!t.enabled}>{t.label}{!t.enabled ? ' (blocked)' : ''}</option>)}
            </select>
          )}
          {task.executionKind === 'deploy' && task.projectId && (
            <select aria-label={`Task ${index + 1} deployment provider`} value={task.deploymentProvider || ''} style={inputStyle} onChange={(e) => onChange(task.key, { deploymentProvider: e.target.value })}>
              <option value="">Choose a deployment provider…</option>
              {(projectProviders[task.projectId] || []).map((p) => <option key={p.id} value={p.id} disabled={!p.configured}>{p.label}{!p.configured ? ' (not configured)' : ''}</option>)}
            </select>
          )}
        </>
      )}
      {task.kind === 'notify_email' && (
        <>
          <input aria-label={`Task ${index + 1} recipient email`} placeholder="Recipient email" value={task.to || ''} style={inputStyle} onChange={(e) => onChange(task.key, { to: e.target.value })} />
          <input aria-label={`Task ${index + 1} subject`} placeholder="Subject" value={task.subject || ''} style={inputStyle} onChange={(e) => onChange(task.key, { subject: e.target.value })} />
          <textarea aria-label={`Task ${index + 1} message`} placeholder="Message" value={task.body || ''} style={{ ...inputStyle, minHeight: '3rem' }} onChange={(e) => onChange(task.key, { body: e.target.value })} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--ozira-font-size-sm, 12px)' }}>
            <input type="checkbox" checked={!!task.requiresApproval} onChange={(e) => onChange(task.key, { requiresApproval: e.target.checked })} /> Require manual approval before sending
          </label>
        </>
      )}
      {task.kind === 'notify_telegram' && (
        <>
          <textarea aria-label={`Task ${index + 1} message`} placeholder="Message" value={task.body || ''} style={{ ...inputStyle, minHeight: '3rem' }} onChange={(e) => onChange(task.key, { body: e.target.value })} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--ozira-font-size-sm, 12px)' }}>
            <input type="checkbox" checked={!!task.requiresApproval} onChange={(e) => onChange(task.key, { requiresApproval: e.target.checked })} /> Require manual approval before sending
          </label>
        </>
      )}
      {task.kind === 'approval' && (
        <input aria-label={`Task ${index + 1} label`} placeholder="What is being approved?" value={task.label || ''} style={inputStyle} onChange={(e) => onChange(task.key, { label: e.target.value })} />
      )}
    </li>
  );
}

let forwardTaskKeySeq = 0;
function newForwardTaskKey() { forwardTaskKeySeq += 1; return 'draft_' + forwardTaskKeySeq; }

export function ForwardWorkspace({ onNavigateProduct, onOpenSettings, organizationTenantId = null }: { onNavigateProduct: (productId: ProductId) => void; onOpenSettings?: () => void; organizationTenantId?: string | null }) {
  const mobile = useMobilePane();

  const [capabilities, setCapabilities] = useState<ForwardCapabilities | null>(null);
  const [plans, setPlans] = useState<ForwardPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [runs, setRuns] = useState<ForwardRun[]>([]);
  const [activeRun, setActiveRun] = useState<ForwardRun | null>(null);
  const [runActionError, setRunActionError] = useState('');
  const [starting, setStarting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [codeProjects, setCodeProjects] = useState<CodeProject[]>([]);
  const [projectTasks, setProjectTasks] = useState<Record<string, CodeTask[]>>({});
  const [projectProviders, setProjectProviders] = useState<Record<string, CodeDeploymentProvider[]>>({});

  const [showBuilder, setShowBuilder] = useState(false);
  const [builderName, setBuilderName] = useState('');
  const [builderTasks, setBuilderTasks] = useState<(ForwardTaskInput & { key: string })[]>([]);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [createPlanError, setCreatePlanError] = useState('');

  const selectedPlan = plans.find((p) => p.id === selectedPlanId) || null;

  useEffect(() => {
    getForwardCapabilities().then(setCapabilities).catch(() => {});
    listForwardPlans().then((list) => {
      setPlans(list);
      if (list.length > 0) setSelectedPlanId((current) => current || list[0].id);
    }).catch(() => {});
    listCodeProjects().then(setCodeProjects).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedPlanId) { setRuns([]); setActiveRun(null); return; }
    listForwardRuns(selectedPlanId).then((list) => {
      setRuns(list);
      setActiveRun(list[0] || null);
    }).catch(() => {});
  }, [selectedPlanId]);

  const pollRun = useCallback((runId: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      getForwardRun(runId).then((run) => {
        setActiveRun(run);
        setRuns((prev) => [run, ...prev.filter((r) => r.id !== run.id)]);
        if (run.status !== 'running' && pollRef.current) clearInterval(pollRef.current);
      }).catch(() => { if (pollRef.current) clearInterval(pollRef.current); });
    }, 1500);
  }, []);

  useEffect(() => {
    if (activeRun && activeRun.status === 'running') pollRun(activeRun.id);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeRun?.id, activeRun?.status, pollRun]);

  const loadProjectCapabilities = useCallback((projectId: string) => {
    if (!projectTasks[projectId]) getCodeTasks(projectId).then((result) => setProjectTasks((prev) => ({ ...prev, [projectId]: result.tasks }))).catch(() => {});
    if (!projectProviders[projectId]) getCodeDeployments(projectId).then((result) => setProjectProviders((prev) => ({ ...prev, [projectId]: result.providers }))).catch(() => {});
  }, [projectTasks, projectProviders]);

  const updateDraftTask = useCallback((key: string, patch: Partial<ForwardTaskInput>) => {
    setBuilderTasks((prev) => prev.map((task) => (task.key === key ? { ...task, ...patch } : task)));
  }, []);
  const removeDraftTask = useCallback((key: string) => setBuilderTasks((prev) => prev.filter((task) => task.key !== key)), []);
  const addDraftTask = useCallback(() => setBuilderTasks((prev) => [...prev, { key: newForwardTaskKey(), kind: 'code_execution' }]), []);

  const handleCreatePlan = useCallback(() => {
    if (!builderName.trim() || !builderTasks.length) return;
    setCreatingPlan(true);
    setCreatePlanError('');
    const tasks: ForwardTaskInput[] = builderTasks.map(({ key: _key, ...task }) => task);
    createForwardPlan({ name: builderName.trim(), tasks }).then((plan) => {
      setPlans((prev) => [plan, ...prev]);
      setSelectedPlanId(plan.id);
      setShowBuilder(false);
      setBuilderName('');
      setBuilderTasks([]);
    }).catch((error) => setCreatePlanError(error?.message || 'Could not create plan.'))
      .finally(() => setCreatingPlan(false));
  }, [builderName, builderTasks]);

  const handleRunPlan = useCallback(() => {
    if (!selectedPlan) return;
    setStarting(true);
    setRunActionError('');
    runForwardPlan(selectedPlan.id).then((run) => {
      setActiveRun(run);
      setRuns((prev) => [run, ...prev]);
    }).catch((error) => setRunActionError(error?.message || 'Could not start the run.'))
      .finally(() => setStarting(false));
  }, [selectedPlan]);

  const handleApprove = useCallback(() => {
    if (!activeRun) return;
    setRunActionError('');
    approveForwardRun(activeRun.id).then(setActiveRun).catch((error) => setRunActionError(error?.message || 'Could not approve this step.'));
  }, [activeRun]);
  const handleReject = useCallback(() => {
    if (!activeRun) return;
    setRunActionError('');
    rejectForwardRun(activeRun.id).then(setActiveRun).catch((error) => setRunActionError(error?.message || 'Could not reject this step.'));
  }, [activeRun]);
  const handleCancelRun = useCallback(() => {
    if (!activeRun) return;
    setRunActionError('');
    cancelForwardRun(activeRun.id).then(setActiveRun).catch((error) => setRunActionError(error?.message || 'Could not cancel this run.'));
  }, [activeRun]);

  const pendingStepIndex = activeRun && activeRun.status === 'awaiting_approval' ? activeRun.currentStepIndex : -1;

  return (
    <ProductSidebarShell
      productId="forward"
      organizationTenantId={organizationTenantId}
      onNavigateProduct={onNavigateProduct}
      onOpenSettings={onOpenSettings}
      recentSlot={(
        <RecentFilesSection
          title="Plans"
          emptyMessage="No plans yet."
          items={plans.map((plan) => ({ id: plan.id, label: plan.name, meta: `${plan.tasks.length} task${plan.tasks.length === 1 ? '' : 's'}` }))}
        />
      )}
    >
    <section className="ozira-product-workspace-layout" style={workspaceStyle} aria-label="OZIRA Forward workflow workspace" data-figma-node="66:223">
      <MobilePaneToolbar labels={['Plans', 'Operations']} activePane={mobile.pane} onOpen={mobile.openPane} />
      <MobileSheetBackdrop open={Boolean(mobile.pane)} onClose={mobile.closePane} />
      <div className="ozira-product-workspace-row ozira-forward-workspace__row" style={workspaceRowStyle}>
        <aside
          className={`ozira-product-secondary-pane${mobile.pane === 'Plans' ? ' is-mobile-open' : ''}`}
          style={{ ...panelStyle, flex: '0.8 1 14rem', minBlockSize: '36.875rem' }}
          data-figma-node="66:299"
          id={mobilePaneId('Plans')}
          role={mobile.pane === 'Plans' ? 'dialog' : undefined}
          aria-modal={mobile.pane === 'Plans' ? true : undefined}
          aria-label={mobile.pane === 'Plans' ? 'Plans' : undefined}
        >
          <button type="button" className="ozira-mobile-sheet-close" onClick={mobile.closePane} aria-label="Close Plans">Close</button>
          <OziraShellDrawer title="Plans" side="start">
            <button type="button" style={codeSmallButtonStyle} onClick={() => setShowBuilder((v) => !v)}>{showBuilder ? 'Cancel' : 'New plan'}</button>
            {plans.length === 0 ? (
              <TruthfulState state="DESIGN_SLOT" title="No plan selected">Create a plan to begin.</TruthfulState>
            ) : (
              <ul className="ozira-panel-list" aria-label="Plans">
                {plans.map((plan) => (
                  <li key={plan.id}>
                    <button type="button" aria-current={plan.id === selectedPlanId} onClick={() => setSelectedPlanId(plan.id)}>{plan.name}</button>
                  </li>
                ))}
              </ul>
            )}
          </OziraShellDrawer>
        </aside>

        <section
          style={{ ...mainPanelStyle, flex: '2.8 1 38rem', minBlockSize: '36.875rem', background: colors.surfaceAlt }}
          aria-label="Workflow"
          data-figma-node="66:302"
          className="ozira-product-main-panel"
          inert={mobile.pane ? '' : undefined}
          aria-hidden={mobile.pane ? true : undefined}
        >
          <div style={panelHeaderStyle}>
            <div>
              <h2 style={panelTitleStyle}>Workflow</h2>
            </div>
            {selectedPlan && !showBuilder && (
              <button type="button" style={codeSmallButtonStyle} disabled={starting || activeRun?.status === 'running' || activeRun?.status === 'awaiting_approval'} onClick={handleRunPlan}>
                {starting ? 'Starting…' : 'Run'}
              </button>
            )}
          </div>

          {showBuilder ? (
            <div style={{ display: 'grid', gap: 'var(--ozira-space-sm, 8px)' }}>
              <input aria-label="Plan name" placeholder="Plan name" value={builderName} style={{ font: 'inherit', padding: '6px 8px', border: `1px solid ${colors.border}`, borderRadius: 6 }} onChange={(e) => setBuilderName(e.target.value)} />
              <ul style={{ display: 'grid', gap: 'var(--ozira-space-sm, 8px)', listStyle: 'none', margin: 0, padding: 0 }}>
                {builderTasks.map((task, index) => (
                  <ForwardTaskBuilderRow
                    key={task.key} task={task} index={index}
                    onChange={updateDraftTask} onRemove={removeDraftTask}
                    codeProjects={codeProjects} projectTasks={projectTasks} projectProviders={projectProviders}
                    onProjectPicked={loadProjectCapabilities}
                  />
                ))}
              </ul>
              <button type="button" style={codeSmallButtonStyle} onClick={addDraftTask}>Add task</button>
              {createPlanError && <p role="alert" style={mutedTextStyle}>{createPlanError}</p>}
              <button type="button" style={codeSmallButtonStyle} disabled={creatingPlan || !builderName.trim() || !builderTasks.length} onClick={handleCreatePlan}>
                {creatingPlan ? 'Creating…' : 'Create plan'}
              </button>
            </div>
          ) : !selectedPlan ? (
            <TruthfulState state="DESIGN_SLOT" title="No plan selected">
              Select or create a plan to begin.
            </TruthfulState>
          ) : (
            <>
              {runActionError && <p role="alert" style={mutedTextStyle}>{runActionError}</p>}
              <ol className="ozira-workflow-stepper" aria-label="Plan tasks">
                {selectedPlan.tasks.map((task, index) => {
                  const step = activeRun?.steps[index];
                  return (
                    <li key={task.id}>
                      <span className="ozira-workflow-stepper__index">{String(index + 1).padStart(2, '0')}</span>
                      <span className="ozira-workflow-stepper__label">{forwardTaskSummary(task, codeProjects, projectTasks)}</span>
                      {step && <CapabilityBadge state={step.status === 'completed' ? 'LIVE' : step.status === 'failed' ? 'BLOCKED' : step.status === 'awaiting_approval' ? 'DEGRADED' : 'AVAILABLE'} note={step.status} />}
                    </li>
                  );
                })}
              </ol>
              {pendingStepIndex >= 0 && (
                <div style={{ display: 'flex', gap: 'var(--ozira-space-sm, 8px)' }}>
                  <button type="button" style={codeSmallButtonStyle} onClick={handleApprove}>Approve</button>
                  <button type="button" style={codeSmallButtonStyle} onClick={handleReject}>Reject</button>
                </div>
              )}
              {activeRun && !['completed', 'failed', 'cancelled'].includes(activeRun.status) && (
                <button type="button" style={codeSmallButtonStyle} onClick={handleCancelRun}>Cancel run</button>
              )}
              {!activeRun && (
                <TruthfulState state="AVAILABLE" title="No active run">
                  Click Run to start this plan.
                </TruthfulState>
              )}
              {activeRun?.status === 'failed' && activeRun.steps[activeRun.currentStepIndex]?.error && (
                <TruthfulState state="BLOCKED" title="Run failed">
                  {activeRun.steps[activeRun.currentStepIndex]?.error?.message}
                </TruthfulState>
              )}
            </>
          )}
        </section>

        <aside
          className={`ozira-product-secondary-pane${mobile.pane === 'Operations' ? ' is-mobile-open' : ''}`}
          style={{ ...panelStyle, flex: '1.1 1 18rem', minBlockSize: '36.875rem' }}
          data-figma-node="66:307"
          id={mobilePaneId('Operations')}
          role={mobile.pane === 'Operations' ? 'dialog' : undefined}
          aria-modal={mobile.pane === 'Operations' ? true : undefined}
          aria-label={mobile.pane === 'Operations' ? 'Operations' : undefined}
        >
          <button type="button" className="ozira-mobile-sheet-close" onClick={mobile.closePane} aria-label="Close Operations">
            Close
          </button>
          <OziraShellDrawer title="Operations" side="end">
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto minmax(0, 1fr)',
                gap: 'var(--ozira-space-xs, 4px) var(--ozira-space-md, 16px)',
                margin: 0,
                color: colors.muted,
                fontSize: 'var(--ozira-font-size-sm, 12px)',
              }}
            >
              <dt>Owner</dt><dd style={{ margin: 0 }}>You</dd>
              <dt>Schedule</dt><dd style={{ margin: 0 }}>Not scheduled</dd>
              <dt>Dependencies</dt><dd style={{ margin: 0 }}>{selectedPlan ? selectedPlan.tasks.map((t, i) => `${i + 1}. ${forwardTaskSummary(t, codeProjects, projectTasks)}`).join(' → ') : 'None defined'}</dd>
              <dt>Approvals</dt><dd style={{ margin: 0 }}>{pendingStepIndex >= 0 ? `Step ${pendingStepIndex + 1} awaiting your decision` : 'None pending'}</dd>
              <dt>Run history</dt>
              <dd style={{ margin: 0 }}>
                {runs.length === 0 ? 'No runs' : (
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 4 }}>
                    {runs.slice(0, 10).map((run) => (
                      <li key={run.id}>
                        <button type="button" style={codeSmallButtonStyle} onClick={() => setActiveRun(run)}>{new Date(run.createdAt).toLocaleString()} — {run.status}</button>
                      </li>
                    ))}
                  </ul>
                )}
              </dd>
              <dt>Safe retry</dt><dd style={{ margin: 0 }}>Unavailable</dd>
            </dl>
            {capabilities && (!capabilities.notifyEmail.configured || !capabilities.notifyTelegram.configured) && (
              <TruthfulState state="DESIGN_SLOT" title="Some notification tasks are unavailable">
                {[capabilities.notifyEmail.reason, capabilities.notifyTelegram.reason].filter(Boolean).join(' ')}
              </TruthfulState>
            )}
          </OziraShellDrawer>
        </aside>
      </div>
    </section>
    </ProductSidebarShell>
  );
}
